import React from 'react';
import EventIcon from '@mui/icons-material/Event';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useSalonPublicTheme } from '../../../../context/SalonPublicThemeContext';
import { HIGH_DENSITY_TABLET_MEDIA } from '../../../../utils/responsiveMedia';
import SalonPublicButton from '../../salonPublicAppearance/SalonPublicButton';


const BOOKING_VARIANTS = new Set(['hero', 'sticky', 'inline', 'floating']);

const SalonTemplateBookingAction = ({
  data,
  variant = 'hero',
  visible = true,
  mode = 'public',
  onBook,
  lightSurface = false,
}) => {
  const { t } = useTranslation();
  const { tokens: theme } = useSalonPublicTheme();
  const bookingVariant = BOOKING_VARIANTS.has(variant) ? variant : 'hero';
  const preview = mode === 'preview';

  if (!visible || !data?.available) return null;

  return (
    <Box
      data-salon-template-block="booking-action"
      data-variant={bookingVariant}
      data-surface={lightSurface ? 'light' : 'overlay'}
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
    >
      <Box
        data-salon-text-role="content"
        sx={{
          backgroundColor: lightSurface ? 'transparent' : 'rgba(255,255,255,0.15)',
          backdropFilter: lightSurface ? 'none' : 'blur(20px)',
          WebkitBackdropFilter: lightSurface ? 'none' : 'blur(20px)',
          borderRadius: 3,
          p: lightSurface ? { xs: 1, md: 2 } : 4,
          [HIGH_DENSITY_TABLET_MEDIA]: { p: lightSurface ? 2 : 5, maxWidth: 460 },
          textAlign: 'center',
          boxShadow: lightSurface ? 'none' : '0 8px 32px rgba(0,0,0,0.3)',
          border: lightSurface ? 'none' : '1px solid rgba(255,255,255,0.3)',
          maxWidth: 400,
          width: '100%',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: lightSurface ? 'transparent' : 'rgba(255,255,255,0.25)',
            transform: lightSurface ? 'none' : 'translateY(-8px)',
            boxShadow: lightSurface ? 'none' : '0 12px 40px rgba(0,0,0,0.4)',
            border: lightSurface ? 'none' : '1px solid rgba(255,255,255,0.5)',
          },
        }}
      >
        <Typography
          data-testid="salon-booking-prompt"
          variant="body1"
          sx={{
            color: lightSurface ? theme.salon.secondary : 'white',
            mb: lightSurface ? 2 : 3,
            fontSize: '1rem',
            [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '1.08rem' },
            lineHeight: 1.5,
            fontWeight: lightSurface ? 700 : 500,
            textShadow: lightSurface ? 'none' : '0 2px 4px rgba(0,0,0,0.3)',
          }}
        >
          {t('landing_salon_details.booking_prompt')}
        </Typography>
        <SalonPublicButton
          preserveLegacyStyle
          variant="contained"
          size="large"
          startIcon={<EventIcon />}
          onClick={onBook}
          disabled={preview}
          aria-disabled={String(preview)}
          fullWidth
          sx={{
            backgroundColor: lightSurface ? theme.salon.primary : 'rgba(255,255,255,0.9)',
            color: lightSurface ? theme.salon.onPrimary : theme.colors.primary[600],
            fontWeight: 700,
            fontSize: '1rem',
            [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '1.08rem', py: 1.7 },
            textTransform: 'none',
            px: 4,
            py: 1.5,
            borderRadius: '10px',
            boxShadow: lightSurface
              ? `0 8px 18px ${theme.salon.primary}2E`
              : '0 4px 12px rgba(255,255,255,0.3)',
            border: lightSurface
              ? `2px solid ${theme.salon.primary}`
              : '2px solid rgba(255,255,255,0.5)',
            '&:hover': {
              backgroundColor: lightSurface ? theme.colors.primary[700] : 'white',
              color: lightSurface ? theme.salon.onPrimary : theme.colors.primary[700],
              transform: 'translateY(-4px) scale(1.02)',
              boxShadow: lightSurface
                ? `0 10px 24px ${theme.salon.primary}3D`
                : '0 8px 24px rgba(255,255,255,0.5)',
              border: lightSurface
                ? `2px solid ${theme.colors.primary[700]}`
                : '2px solid white',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {t('landing_salon_details.book_appointment')}
        </SalonPublicButton>
      </Box>
    </Box>
  );
};

export default SalonTemplateBookingAction;
